<template>
  <FormKit
    ref="selectCoverageForm"
    name="selectCoverageForm"
    v-slot="{ state: { valid } }"
    type="form"
    :actions="false"
    @submit="buttonClicked"
  >
    <div class="flex flex-col gap-5 text-center" data-test-id="combined_policy_modal">
      <RichText :field="fields.title" class="text-lg-bold mt-4 mb-1 px-8" />
      <RichText :field="fields.subtitle" class="text-m px-0 md:px-12" />

      <RadioCardList v-model="selectedCoverage" :bindings="coverageBindings" />
      <InformationBox
        v-if="disabledCoveragesMessage && hasDisabled"
        :hideIcon="true"
        :description="disabledCoveragesMessage?.content?.value"
        :type="disabledCoveragesMessage?.messageType?.value"
      ></InformationBox>
      <button :disabled="!valid" :data-test-id="`${dataTestId}_NextButton`" class="btn btn-primary btn-md mt-5">
        {{ fields.modalBtnText?.value }}
      </button>
    </div>
  </FormKit>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  Coverage,
  getInsuranceTypeDisplayName,
  InsuranceQuoteType,
  IRadioButtonCard,
  PetCoverage,
  QuoteTypeCodes,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import RadioCardList from '../RadioCardList/RadioCardList.vue';
import { useI18n } from 'vue-i18n';
import InformationBox from '../InformationBox/InformationBox.vue';

export interface ISelectCoverageOption {
  coverages: Coverage[];
  modelValue: string;
  fields: {
    title: Field<string>;
    subtitle: Field<string>;
    homeIcon?: Field<ImageFieldValue>;
    homeContentsIcon?: Field<ImageFieldValue>;
    contentsIcon: Field<ImageFieldValue>;
    motorIcon?: Field<ImageFieldValue>;
    motorThirdPartyIcon?: Field<ImageFieldValue>;
    motorThirdPartyFireIcon: Field<ImageFieldValue>;
    boatIcon?: Field<ImageFieldValue>;
    caravanIcon?: Field<ImageFieldValue>;
    motorcycleIcon?: Field<ImageFieldValue>;
    petIcon?: Field<ImageFieldValue>;
    modalBtnText?: Field<string>;
  };
  disableCoverageCodes?: string[];
  disabledCoveragesMessage?: any;
  dataTestId?: string;
}
const props = defineProps<ISelectCoverageOption>();

const selectCoverageForm = ref<any>(null);

const emit = defineEmits(['update:modelValue', 'buttonClicked']);

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const selectedCoverage = ref('');

watch(selectedCoverage, (newVal) => {
  if (newVal) {
    emit('update:modelValue', newVal);
  }
});

watch(props, (newVal) => {
  //Clear selection when re-opening modal.
  if (!newVal.modelValue && selectedCoverage.value) {
    selectedCoverage.value = '';

    //Clear validation when re-opening.
    const node = selectCoverageForm.value?.node;
    if (node) {
      node.reset();
    }
  }
});

const buttonClicked = () => {
  emit('buttonClicked');
};

const hasDisabled = computed(() => {
  return radioOptions?.value?.some((x) => x.disabled);
});

const coverageBindings = computed(() => {
  if (props.coverages?.length) {
    return {
      name: 'selectcoverage',
      id: 'selectCoverage',
      optionsClass: 'racq-max-w-2xl !mt-0',
      options: radioOptions.value,
      isComparison: true,
      validation: [['required']],
      validationMessages: {
        required: 'Please select a coverage.',
      },
    };
  }
  return undefined;
});

const radioOptions = computed((): IRadioButtonCard[] =>
  props.coverages.map(
    (x: Coverage): IRadioButtonCard => ({
      label: getInsuranceTypeDisplayName(dictionary, x?.coverageCode),
      help: getSubtitle(x),
      image: { value: getProductTypeIcon(x.coverageCode) },
      value: x.id,
      disabled: (props?.disableCoverageCodes ?? [])?.indexOf(x?.coverageCode) > -1,
    })
  )
);

const getProductTypeIcon = (coverageCode?: string): ImageFieldValue | undefined => {
  if (coverageCode) {
    switch (coverageCode) {
      case QuoteTypeCodes[InsuranceQuoteType.Contents]:
        return props.fields.contentsIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Home]:
        return props.fields.homeIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
        return props.fields.homeContentsIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
        return props.fields.motorIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
        return props.fields.motorThirdPartyFireIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
        return props.fields.motorThirdPartyIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
        return props.fields.petIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive]:
      case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty]:
        return props.fields.boatIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Caravan]:
        return props.fields.caravanIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Motorcycle]:
        return props.fields.motorcycleIcon?.value;
    }
  }
  return props.fields.motorIcon?.value;
};

const getSubtitle = (coverage: Coverage): string => {
  if (coverage?.isHome) {
    return (coverage as ResidentialCoverage)?.riskAddress?.formattedLong;
  } else if (coverage?.isPet) {
    return `${(coverage as PetCoverage)?.petName}, ${(coverage as PetCoverage)?.petBreed}`;
  } else {
    return (coverage as VehicleCoverage)?.vehicle?.formattedSummary;
  }
};
</script>
